/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { X, Play, Pause, SkipForward } from "lucide-react";

export const KIOSK_KEY = "bc_kiosk";
const click = (sel) => { const el = document.querySelector(`[data-testid="${sel}"]`); if (el) el.click(); return Boolean(el); };

const STEPS = [
  { path: "/app", d: 7000, titre: "Accueil", texte: "Programme du jour, badges et objectifs fixés par le praticien — tout fonctionne hors-ligne." },
  { path: "/app/exercises", d: 4000, titre: "Exercices", texte: "Trois domaines : équilibre & mémoire, marche, gestes du quotidien." },
  { path: "/app/exercises", d: 4500, titre: "Aperçu illustré", texte: "Chaque exercice est illustré étape par étape.", action: () => click("domain-marche-card") },
  { path: "/app/exercises", d: 5500, titre: "Lecteur guidé", texte: "Consigne courte, bouton Écouter, vidéo de démonstration si en ligne.", action: () => click("start-assis_debout") },
  { path: "/app/exercises", d: 4500, titre: "Étape suivante", texte: "Navigation simple : Précédent / Suivant, gros boutons.", action: () => click("exercise-next-btn") },
  { path: "/app/exercises", d: 1000, titre: "", texte: "", action: () => click("exercise-close-btn") || click("domain-preview-back") },
  { path: "/app/health", d: 6500, titre: "Ma santé", texte: "Tension en gros chiffres, médicaments, humeur, habitudes : prévention de la récidive." },
  { path: "/app/communicate", d: 6500, titre: "Communiquer", texte: "Carnet de mots pictographique pour l'aphasie, avec synthèse vocale." },
  { path: "/app/profile", d: 6500, titre: "Profil", texte: "Mode Aidant, français / lingala, abonnement, téléconsultation, données RGPD." },
];

export default function KioskController() {
  const navigate = useNavigate();
  const { setAidantMode } = useApp();
  const { user } = useAuth();
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const active = typeof window !== "undefined" && localStorage.getItem(KIOSK_KEY) === "1" && user?.role !== "therapeute";

  useEffect(() => {
    if (!active || paused) return undefined;
    const step = STEPS[i];
    navigate(step.path);
    const t1 = setTimeout(() => step.action && step.action(), 700);
    timer.current = setTimeout(() => setI((i + 1) % STEPS.length), step.d);
    return () => { clearTimeout(t1); clearTimeout(timer.current); };
  }, [i, paused, active]); // eslint-disable-line

  if (!active) return null;
  const step = STEPS[i];
  const stop = () => { localStorage.removeItem(KIOSK_KEY); click("exercise-close-btn"); click("domain-preview-back"); setAidantMode(false); navigate("/app"); window.location.reload(); };
  const root = document.getElementById("bc-modal-root") || document.body;

  return createPortal(
    <div data-testid="kiosk-banner" className="fixed left-1/2 -translate-x-1/2 bottom-[92px] z-[70] w-[min(400px,calc(100%-24px))] rounded-3xl bg-[#0A1426]/95 text-white backdrop-blur-md p-4 shadow-2xl pointer-events-auto">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#D4AF5A] font-bold">Mode démonstration · {i + 1}/{STEPS.length}</div>
          {step.titre && <div className="font-[Fraunces] text-lg font-bold leading-tight">{step.titre}</div>}
          <p className="text-[12px] text-white/80 leading-snug">{step.texte}</p>
        </div>
        <div className="flex flex-col gap-1">
          <button data-testid="kiosk-pause" onClick={() => setPaused((p) => !p)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">{paused ? <Play size={16} /> : <Pause size={16} />}</button>
          <button data-testid="kiosk-next" onClick={() => setI((i + 1) % STEPS.length)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center"><SkipForward size={16} /></button>
          <button data-testid="kiosk-exit" onClick={stop} className="w-9 h-9 rounded-full bg-[#D9534F] grid place-items-center"><X size={16} /></button>
        </div>
      </div>
      <div className="h-1 bg-white/15 rounded-full mt-3 overflow-hidden">
        <div key={i} className="h-full bg-[#D4AF5A]" style={{ animation: paused ? "none" : `kiosk-progress ${step.d}ms linear forwards` }} />
      </div>
    </div>,
    root,
  );
}
